import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export class ImageUploadService {
  /**
   * Upload une image vers Firebase Storage
   * @param file - Le fichier image à uploader
   * @param path - Le chemin de stockage (ex: 'candidates/photo123.jpg')
   * @returns Promise<UploadResult>
   */
  static async uploadImage(file: File, path: string): Promise<UploadResult> {
    try {
      // Validation du fichier
      if (!file.type.startsWith('image/')) {
        return {
          success: false,
          error: 'Le fichier doit être une image'
        };
      }

      // Limite de taille (5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        return {
          success: false,
          error: 'Le fichier ne doit pas dépasser 5MB'
        };
      }

      // Créer une référence vers le fichier
      const storageRef = ref(storage, path);
      
      // Upload du fichier
      const snapshot = await uploadBytes(storageRef, file);
      
      // Récupérer l'URL de téléchargement
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        success: true,
        url: downloadURL
      };
    } catch (error: any) {
      console.error('Erreur lors de l\'upload:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de l\'upload de l\'image'
      };
    }
  }

  /**
   * Supprime une image de Firebase Storage
   * @param url - L'URL de l'image à supprimer
   * @returns Promise<boolean>
   */
  static async deleteImage(url: string): Promise<boolean> {
    try {
      // Extraire le chemin du fichier depuis l'URL
      const path = this.extractPathFromUrl(url);
      if (!path) {
        console.error('Impossible d\'extraire le chemin depuis l\'URL');
        return false;
      }

      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      return true;
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      return false;
    }
  }

  /**
   * Génère un nom de fichier unique
   * @param originalName - Le nom original du fichier
   * @param prefix - Préfixe pour le dossier (ex: 'candidates', 'admins')
   * @returns string
   */
  static generateUniqueFileName(originalName: string, prefix: string): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    return `${prefix}/${timestamp}_${randomString}.${extension}`;
  }

  /**
   * Extrait le chemin du fichier depuis une URL Firebase Storage
   * @param url - L'URL Firebase Storage
   * @returns string | null
   */
  private static extractPathFromUrl(url: string): string | null {
    try {
      // URL Firebase Storage format: https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile?alt=media&token=...
      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
      if (pathMatch) {
        return decodeURIComponent(pathMatch[1]);
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de l\'extraction du chemin:', error);
      return null;
    }
  }

  /**
   * Redimensionne une image côté client (optionnel)
   * @param file - Le fichier image
   * @param maxWidth - Largeur maximale
   * @param maxHeight - Hauteur maximale
   * @param quality - Qualité de compression (0-1)
   * @returns Promise<File>
   */
  static async resizeImage(
    file: File, 
    maxWidth: number = 400, 
    maxHeight: number = 400, 
    quality: number = 0.8
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculer les nouvelles dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Redimensionner le canvas
        canvas.width = width;
        canvas.height = height;

        // Dessiner l'image redimensionnée
        ctx?.drawImage(img, 0, 0, width, height);

        // Convertir en blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Erreur lors du redimensionnement'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Erreur lors du chargement de l\'image'));
      img.src = URL.createObjectURL(file);
    });
  }
}
